import React from 'react';
import {useMatches} from '@remix-run/react';
import type {
  Product,
  ProductVariant,
  Image as ImageType,
  Maybe,
} from '@shopify/hydrogen/storefront-api-types';
import * as R from 'ramda';
import {useLocation} from '@remix-run/react';
import clsx from 'clsx';
import {Image} from '@shopify/hydrogen';

//components
import {Link} from 'components/Link';
import {optionsFromSearch, productVariantPath} from '~/lib/variantUrl';

//domains
import type {SanityProductQueryResponse} from '~/domains/product';

export default function ProductDetailsSize({
  selectedVariant,
}: {
  selectedVariant: ProductVariant;
}) {
  const matches = useMatches();
  const routeData = matches.find(
    (m) => m.id === 'routes/($lang)/products/$productHandle',
  )?.data;
  const product = routeData.product as Product & {
    selectedVariant?: ProductVariant;
  };
  const productSettings =
    routeData.productSettings as SanityProductQueryResponse['productSettings'];

  const {search, pathname} = useLocation() as {
    search: string;
    pathname: string;
  };
  const params = new URLSearchParams(search);
  const selectedColour =
    params.get('Colour') ||
    params.get('Color') ||
    selectedVariant?.selectedOptions.find(
      (o) => o.name === 'Colour' || o.name === 'Color',
    )?.value;
  const selectedColourFormatted =
    selectedColour && selectedColour.replace('+', ' ');

  if (product.options) {
    return (
      <div className="grid grid-cols-1 gap-8">
        {product.options.map((o) => {
          switch (o.name) {
            case 'Size':
              return (
                <div
                  key="product-details-size"
                  className="product-options--size order-1 lg:order-2"
                >
                  <span className="mb-3 block font-medium">
                    {productSettings?.product_settings_translations
                      ?.size_label ?? 'Size'}{' '}
                    :
                  </span>
                  <div className="my-2">
                    <ul className="flex flex-wrap">
                      {o.values &&
                        o.values.map((ot, index) => {
                          const variantsOfThisColour =
                            product.variants.nodes.filter(
                              (v) =>
                                v.selectedOptions.find(
                                  (vo) =>
                                    vo.name === 'Colour' || vo.name === 'Color',
                                )?.value === selectedColourFormatted,
                            );

                          const isSizeOfThisColourAvailable =
                            variantsOfThisColour.find(
                              (v) =>
                                v.availableForSale &&
                                v.selectedOptions.find(
                                  (vo) => vo.name === 'Size',
                                )?.value === ot,
                            );

                          return (
                            <SizeOption
                              optionName={o.name}
                              key={`${ot}-${index}`}
                              option={ot}
                              available={!R.isNil(isSizeOfThisColourAvailable)}
                              selectedVariant={selectedVariant}
                            />
                          );
                        })}
                    </ul>
                  </div>
                </div>
              );
            case 'Colour':
            case 'Color':
              return <></>;
            default:
              return (
                <div
                  key="product-details-options"
                  className="product-options--others order-1 lg:order-2"
                >
                  <span className="mb-3 block font-medium">{o.name}:</span>
                  <div className="my-2">
                    <ul className="flex flex-wrap">
                      {o.values &&
                        o.values.map((ot, index) => {
                          const optionIsAvailable = product.variants.nodes.find(
                            (v) =>
                              v.selectedOptions.find((vo) => vo.name === o.name)
                                ?.value === ot,
                          )?.availableForSale;

                          return (
                            <Option
                              optionName={o.name}
                              key={`${ot}-${index}`}
                              option={ot}
                              available={Boolean(optionIsAvailable)}
                              selectedVariant={selectedVariant}
                            />
                          );
                        })}
                    </ul>
                  </div>
                </div>
              );
          }
        })}
      </div>
    );
  }
  return <></>;
}

function SizeOption({
  option,
  available,
  optionName,
  selectedVariant,
}: {
  option: string;
  available: boolean;
  optionName: string;
  selectedVariant: ProductVariant;
}) {
  const {search, pathname} = useLocation() as {
    search: string;
    pathname: string;
  };
  const params = new URLSearchParams(search);
  const isActive =
    selectedVariant?.selectedOptions.find((o) => o.name === optionName)
      ?.value === option ?? params.get(optionName) === option;
  const selectedColour = selectedVariant.selectedOptions.find(
    (o) => o.name === 'Colour' || o.name === 'Color',
  )?.value;
  const colorOptionName = selectedVariant.selectedOptions.find(
    (o) => o.name === 'Colour' || o.name === 'Color',
  )?.name;
  const options = optionsFromSearch(
    search,
    selectedVariant.selectedOptions.map((o) => o.name),
  );
  options[optionName] = option;

  if (colorOptionName && selectedColour) {
    options[colorOptionName] = selectedColour;
  }

  return (
    <li
      title={option}
      className={clsx([
        'product-variant--size flex mr-2 mb-2',
        !available && 'pointer-events-none opacity-25',
      ])}
    >
      <Link
        to={productVariantPath(pathname.replace('en/', ''), options)}
        className="block w-full"
      >
        <div className="product-variant__image relative text-white">
          <div
            className={clsx([
              'flex justify-center min-h-[38px] min-w-[50px] cursor-pointer items-center rounded-[6px] bg-black p-2 text-center text-[20px] leading-none',
              available && isActive && 'text-hk-orange',
              // (selectedColour && sizeInColour?.available && checked) ||
              // (size && size.availableForSale && checked)
              //   ? 'text-hk-orange'
              //   : 'text-white',
            ])}
          >
            <span className="">{option}</span>
          </div>
        </div>
      </Link>
    </li>
  );
}

function Option({
  option,
  available,
  optionName,
  selectedVariant,
}: {
  option: string;
  available: boolean;
  optionName: string;
  selectedVariant: ProductVariant;
}) {
  const {search, pathname} = useLocation() as {
    search: string;
    pathname: string;
  };
  const params = new URLSearchParams(search);
  const isActive =
    selectedVariant?.selectedOptions.find((o) => o.name === optionName)
      ?.value === option ?? params.get(optionName) === option;

  const options = optionsFromSearch(
    search,
    selectedVariant.selectedOptions.map((o) => o.name),
  );
  options[optionName] = option;

  return (
    <li
      title={option}
      className={clsx([
        'product-variant--size flex mr-2 mb-2',
        !available && 'pointer-events-none opacity-25',
      ])}
    >
      <Link
        to={productVariantPath(pathname.replace('en/', ''), options)}
        className="block w-full"
      >
        <div className="product-variant__image relative text-white">
          <div
            className={clsx([
              'flex justify-center min-h-[38px] min-w-[50px] cursor-pointer items-center rounded-[6px] bg-black p-2 text-center text-[20px] leading-none',
              available && isActive && 'text-hk-orange',
              // (selectedColour && sizeInColour?.available && checked) ||
              // (size && size.availableForSale && checked)
              //   ? 'text-hk-orange'
              //   : 'text-white',
            ])}
          >
            <span className="text-[16px]">{option}</span>
          </div>
        </div>
      </Link>
    </li>
  );
}
